import React, { useEffect, useState, useCallback } from 'react';
import { 
  Users, Upload, X, RefreshCw, FileText, RotateCcw, 
  Search, Clock, Layers, Activity, ChevronRight, Zap,
  CheckCircle2, AlertCircle, HelpCircle, HardDrive, Terminal
} from 'lucide-react';
import { PostPublishMultiple } from './apis/post';
import { GetPublishMultipleService } from './apis/get';
import toast from 'react-hot-toast';
import LoaderService from '../services/LoaderService';

interface PublishSideMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PublishResult {
  error?: string;
  executionTime?: number;
  successInfo?: {
    message?: string;
    published_count?: number;
    already_published?: number;
    total_found?: number;
  };
}

const QuerySideMenu: React.FC<PublishSideMenuProps> = ({ isOpen, onClose }) => {
  const [csvData, setCsvData] = useState<{userId: string, candidateId: string}[]>([]);
  const [result, setResult] = useState<PublishResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inputMode, setInputMode] = useState<'csv' | 'json'>('json');
  const [jsonInput, setJsonInput] = useState('');
  
  // Service Status Filters
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [jobStatusFilter, setJobStatusFilter] = useState('');
  const [jobData, setJobData] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    if (!isOpen) return;
    setIsLoading(true);
    try {
      const response = await GetPublishMultipleService({
        limit: 50,
        offset: 0,
        status: jobStatusFilter || undefined,
        user_id: jobSearchTerm.trim() || undefined
      });

      if (response.status === 'success' || response.status === true) {
        // ROBUST DATA EXTRACTION: Handle both object wrapping and direct arrays
        const jobs = Array.isArray(response.data) 
          ? response.data 
          : (response.data?.jobs || []);
        
        // Apply local sort if needed, though backend usually handles it
        const sortedJobs = [...jobs].sort((a, b) => 
          new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
        
        setJobData(sortedJobs);
      } else {
        toast.error('Failed to sync node registry');
      }
    } catch (error: any) {
      console.error('Registry sync error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isOpen, jobSearchTerm, jobStatusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const data = lines.slice(1).map(line => {
        const [userId, candidateId] = line.split(',').map(s => s.trim());
        return { userId, candidateId };
      }).filter(item => item.userId && item.candidateId);
      setCsvData(data);
    };
    reader.readAsText(file);
  };

  const resetInput = () => {
    setCsvData([]);
    setJsonInput('');
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const publishMatches = async () => {
    let matchesPayload: any[] = [];

    if (inputMode === 'csv') {
      if (csvData.length === 0) return;
      matchesPayload = csvData.map(item => ({
        user_id: item.userId,
        candidate_id: item.candidateId
      }));
    } else {
      if (!jsonInput.trim()) return;
      try {
        const parsed = JSON.parse(jsonInput);
        matchesPayload = parsed.matches || (Array.isArray(parsed) ? parsed : [parsed]);
      } catch (e) {
        setResult({ error: 'Invalid JSON Protocol' });
        toast.error('JSON Encoding Error');
        return;
      }
    }

    setIsLoading(true);
    setResult(null);
    LoaderService.show();

    try {
      const startTime = Date.now();
      const response = await PostPublishMultiple({ matches: matchesPayload });
      const executionTime = Date.now() - startTime;
      
      if (response.status === true || response.success === true) {
        setResult({
          executionTime,
          successInfo: {
            message: response.message || 'Execution Complete',
            published_count: response.data?.successful || response.published_count,
            already_published: response.already_published,
            total_found: response.data?.total || response.total_found
          }
        });
        toast.success(`Success: ${response.data?.successful || 'Cluster'} Published`);
        if (inputMode === 'csv') resetInput();
        fetchData();
      } else {
        setResult({ error: response.message || 'Node Rejected Request' });
        toast.error(response.message || 'Execution Failed');
      }
    } catch (error: any) {
      setResult({ error: 'Internal Engine Failure' });
      toast.error('Network Protocol Error');
    } finally {
      setIsLoading(false);
      LoaderService.hide();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.1)]';
      case 'processing': return 'text-amber-400 bg-amber-500/10 border-amber-500/20 shadow-[0_0_15px_rgba(245,158,11,0.1)]';
      case 'failed': return 'text-rose-400 bg-rose-500/10 border-rose-500/20 shadow-[0_0_15px_rgba(244,63,94,0.1)]';
      case 'pending': return 'text-blue-400 bg-blue-500/10 border-blue-500/20 shadow-[0_0_15px_rgba(59,130,246,0.1)]';
      default: return 'text-slate-500 bg-slate-900 border-slate-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle2 size={10} />;
      case 'processing': return <RefreshCw size={10} className="animate-spin" />;
      case 'failed': return <AlertCircle size={10} />;
      case 'pending': return <Clock size={10} />;
      default: return <Clock size={10} />;
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md z-[60] transition-opacity duration-500" onClick={onClose} />
      )}

      {/* LEFT: SERVICE STATUS MONITOR */}
      <div className={`fixed left-0 top-0 h-full w-[450px] bg-[#020617] border-r border-slate-800/60 text-white shadow-[30px_0_60px_rgba(0,0,0,0.8)] transform transition-all duration-700 cubic-bezier(0.4, 0, 0.2, 1) z-[70] ${isOpen ? 'translate-x-0' : '-translate-x-full opacity-0'}`}>
        <div className="flex flex-col h-full">
          {/* Header Area */}
          <div className="p-8 bg-gradient-to-b from-slate-900/80 to-transparent border-b border-slate-800/40">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-500/10 rounded-2xl text-blue-400 border border-blue-500/20 shadow-inner">
                  <Terminal size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tighter italic text-white flex items-center gap-2">
                    Service Job <span className="text-blue-500">Status</span>
                  </h2>
                  <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mt-1">Recent Activity</p>
                </div>
              </div>
              <button 
                onClick={fetchData} 
                className="p-3 bg-slate-900/50 hover:bg-blue-500/10 hover:text-blue-400 rounded-xl border border-slate-800 transition-all active:scale-90"
              >
                <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              </button>
            </div>

            {/* Micro Filters */}
            <div className="flex gap-3">
              <div className="relative flex-1 group">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-600 group-focus-within:text-blue-500 transition-colors" size={14} />
                <input 
                  type="text" 
                  placeholder="SEARCH USER ID..."
                  value={jobSearchTerm}
                  onChange={(e) => setJobSearchTerm(e.target.value.toUpperCase())}
                  className="w-full bg-slate-950/80 border border-slate-800 rounded-2xl py-3 pl-11 pr-4 text-[10px] font-black uppercase tracking-widest focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all placeholder:text-slate-800" 
                />
              </div>
              <select 
                value={jobStatusFilter}
                onChange={(e) => setJobStatusFilter(e.target.value)}
                className="bg-slate-950/80 border border-slate-800 rounded-2xl px-4 py-3 text-[10px] font-black uppercase tracking-widest outline-none focus:ring-1 focus:ring-blue-500/50 transition-all appearance-none cursor-pointer text-slate-400 hover:text-white"
              >
                <option value="">STATUS: ALL</option>
                <option value="pending">PENDING</option>
                <option value="processing">PROCESSING</option>
                <option value="completed">COMPLETED</option>
                <option value="failed">FAILED</option>
              </select>
            </div>
          </div>

          {/* Service Status Data Stream */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
            {isLoading && jobData.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center space-y-6">
                 <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/20 blur-2xl rounded-full animate-pulse" />
                    <RefreshCw className="animate-spin text-blue-500 relative" size={40} />
                 </div>
                 <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-700 animate-pulse">Loading Jobs...</span>
              </div>
            ) : jobData.length > 0 ? (
              jobData.map((job) => (
                <div key={job.id} className="group bg-slate-900/20 hover:bg-slate-900/40 border border-slate-800 hover:border-blue-500/40 rounded-3xl p-5 transition-all relative overflow-hidden">
                  {/* Glass highlight */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/5 blur-3xl rounded-full pointer-events-none" />
                  
                  <div className="flex justify-between items-start mb-4 relative">
                    <div className="space-y-1.5">
                       <div className="flex items-center gap-2">
                          <HardDrive size={10} className="text-slate-600" />
                          <span className="text-[9px] font-black text-slate-600 uppercase tracking-widest">Job ID #{job.id}</span>
                       </div>
                       <div className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-widest inline-flex items-center gap-2 border ${getStatusColor(job.status)}`}>
                         {getStatusIcon(job.status)}
                         {job.status}
                       </div>
                    </div>
                    <div className="text-right">
                       <span className="text-[11px] font-black text-blue-400 italic uppercase tracking-tighter">{job.service_name.replace(/_/g, ' ')}</span>
                       <p className="text-[8px] text-slate-600 font-black uppercase mt-1 tracking-widest">{new Date(job.created_at).toLocaleDateString()} {new Date(job.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6 pt-4 border-t border-slate-800/40 relative">
                    <div className="space-y-1">
                      <p className="text-[8px] font-black text-slate-700 uppercase tracking-widest">User ID</p>
                      <p className="text-[11px] font-mono font-bold text-slate-400 group-hover:text-white transition-colors">#{job.user_id}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-[8px] font-black text-slate-700 uppercase tracking-widest">Candidate ID</p>
                      <p className="text-[11px] font-mono font-bold text-slate-400 group-hover:text-white transition-colors">{job.candidate_user_id || '-- SYSTEM --'}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-800 space-y-4 opacity-50">
                <HelpCircle size={50} />
                <span className="text-[10px] font-black uppercase tracking-[0.3em]">No Active Nodes In View</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT: EXECUTION CONTROL CENTER */}
      <div className={`fixed right-0 top-0 h-full w-[450px] bg-[#020617] border-l border-slate-800/60 text-white shadow-[-30px_0_60px_rgba(0,0,0,0.8)] transform transition-all duration-700 cubic-bezier(0.4, 0, 0.2, 1) z-[70] ${isOpen ? 'translate-x-0' : 'translate-x-full opacity-0'}`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-8 bg-gradient-to-b from-slate-900/80 to-transparent border-b border-slate-800/40">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500/10 rounded-2xl text-amber-500 border border-amber-500/20 shadow-inner">
                  <Zap size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-black uppercase tracking-tighter italic text-white flex items-center gap-2">
                    Bulk <span className="text-amber-500">Sync</span>
                  </h2>
                  <p className="text-[9px] font-black text-slate-500 uppercase tracking-[0.3em] mt-1">High Velocity Execution</p>
                </div>
              </div>
              <button 
                onClick={onClose} 
                className="p-3 bg-slate-900/50 hover:bg-rose-500/10 hover:text-rose-500 rounded-xl border border-slate-800 transition-all active:scale-90"
              >
                <X size={20} />
              </button>
            </div>

            {/* Command Toggle */}
            <div className="flex bg-slate-950 p-1.5 rounded-[1.4rem] border border-slate-800/60">
              <button
                onClick={() => setInputMode('json')}
                className={`flex-1 flex items-center justify-center gap-3 py-3 text-[10px] font-black uppercase tracking-widest rounded-2xl transition-all ${inputMode === 'json' ? 'bg-amber-500 text-slate-950 shadow-[0_4px_20px_rgba(245,158,11,0.3)]' : 'text-slate-600 hover:text-slate-300'}`}
              >
                <Terminal size={14} /> JSON Engine
              </button>
              <button
                onClick={() => setInputMode('csv')}
                className={`flex-1 flex items-center justify-center gap-3 py-3 text-[10px] font-black uppercase tracking-widest rounded-2xl transition-all ${inputMode === 'csv' ? 'bg-amber-500 text-slate-950 shadow-[0_4px_20px_rgba(245,158,11,0.3)]' : 'text-slate-600 hover:text-slate-300'}`}
              >
                <Upload size={14} /> CSV Uplink
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
            {inputMode === 'json' ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                   <label className="text-[10px] font-black text-slate-600 uppercase tracking-[0.2em]">Matrix Script</label>
                   <button onClick={resetInput} className="text-[9px] font-black text-rose-500/60 uppercase hover:text-rose-500 transition-colors">Reset Buffer</button>
                </div>
                <textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder='{ "matches": [ { "user_id": "...", "candidate_id": "..." } ] }'
                  className="w-full h-96 bg-slate-950/50 border border-slate-800 rounded-[2rem] p-6 text-[11px] font-mono text-amber-500/80 focus:outline-none focus:ring-1 focus:ring-amber-500/30 transition-all resize-none shadow-inner"
                />
              </div>
            ) : (
              <div className="space-y-6">
                 <div className="p-12 border-2 border-dashed border-slate-800 rounded-[3rem] bg-slate-900/10 flex flex-col items-center justify-center text-center group hover:border-amber-500/40 transition-all relative">
                    <input type="file" accept=".csv" onChange={handleFileUpload} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                    <div className="p-6 bg-slate-950 rounded-full mb-6 group-hover:scale-110 group-hover:bg-amber-500 group-hover:text-slate-950 transition-all duration-500 shadow-xl">
                       <Upload size={36} className="text-slate-600 group-hover:text-inherit" />
                    </div>
                    <h3 className="text-sm font-black text-white uppercase tracking-widest mb-2 italic">Inject CSV Cluster</h3>
                    <p className="text-[9px] text-slate-600 font-bold uppercase tracking-[0.2em]">Expected Header: userId, candidateId</p>
                 </div>
                 
                 {csvData.length > 0 && (
                   <div className="bg-slate-900/30 border border-slate-800 rounded-3xl p-6 animate-in fade-in slide-in-from-bottom-4">
                      <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-800/40">
                         <div className="flex items-center gap-2">
                            <CheckCircle2 size={14} className="text-emerald-500" />
                            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Parsing Success</span>
                         </div>
                         <span className="text-[10px] font-black text-white px-3 py-1 bg-blue-500/20 rounded-xl border border-blue-500/30">{csvData.length} Nodes</span>
                      </div>
                      <div className="max-h-40 overflow-y-auto space-y-2 custom-scrollbar pr-2">
                         {csvData.slice(0, 5).map((row, i) => (
                           <div key={i} className="flex items-center justify-between text-[11px] font-mono text-slate-500 hover:text-white transition-colors">
                              <span className="text-slate-600 italic">#{i+1}</span>
                              <div className="flex items-center gap-4">
                                 <span>{row.userId}</span>
                                 <ChevronRight size={10} className="text-amber-500/40" />
                                 <span>{row.candidateId}</span>
                              </div>
                           </div>
                         ))}
                         {csvData.length > 5 && <p className="text-[9px] text-center text-slate-700 font-black uppercase mt-4 tracking-widest pt-4 border-t border-slate-800/20">+{csvData.length - 5} Supplemental Nodes Detected</p>}
                      </div>
                   </div>
                 )}
              </div>
            )}

            {/* Execution Logs */}
            {result?.successInfo && (
              <div className="p-6 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl space-y-4 animate-in fade-in zoom-in-95">
                 <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                       <CheckCircle2 size={18} className="text-emerald-500" />
                    </div>
                    <span className="text-xs font-black uppercase text-white tracking-widest italic">Sync Execution Success</span>
                 </div>
                 <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800/40">
                       <p className="text-[8px] font-black text-slate-600 uppercase mb-1 tracking-widest">Nodes Affected</p>
                       <p className="text-2xl font-black text-white tracking-tighter">{result.successInfo.published_count}</p>
                    </div>
                    <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800/40 text-right">
                       <p className="text-[8px] font-black text-slate-600 uppercase mb-1 tracking-widest">Node Duration</p>
                       <p className="text-2xl font-black text-white tracking-tighter">{(result.executionTime! / 1000).toFixed(2)}<span className="text-[10px] text-slate-500 ml-1">S</span></p>
                    </div>
                 </div>
              </div>
            )}

            {result?.error && (
              <div className="p-6 bg-rose-500/5 border border-rose-500/20 rounded-3xl flex items-center gap-4 animate-in slide-in-from-right-4">
                 <AlertCircle size={20} className="text-rose-500" />
                 <div>
                    <p className="text-[9px] font-black text-rose-500/60 uppercase tracking-widest mb-1">Execution Interrupted</p>
                    <p className="text-[11px] font-black uppercase text-white tracking-tight">{result.error}</p>
                 </div>
              </div>
            )}
          </div>

          {/* Action Footer */}
          <div className="p-8 bg-gradient-to-t from-slate-950 to-transparent">
            <button
              onClick={publishMatches}
              disabled={isLoading || (inputMode === 'json' ? !jsonInput : csvData.length === 0)}
              className="w-full py-5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-50 hover:to-orange-500 disabled:from-slate-900 disabled:to-slate-900 text-slate-950 text-[11px] font-black uppercase tracking-[0.3em] rounded-[2rem] shadow-2xl shadow-amber-500/20 transition-all active:scale-95 flex items-center justify-center gap-4 group"
            >
              {isLoading ? <RefreshCw className="animate-spin" size={18} /> : <Zap size={18} className="group-hover:scale-125 transition-transform" />}
              {isLoading ? 'INITIATING NODE SYNC...' : `EXECUTE CLUSTER PROTOCOL`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuerySideMenu;